import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Check, X, Search, RefreshCcw } from "lucide-react";
import { SyncStatusPill } from "@/components/clientmaster/SyncStatusPill";

const pickLink = (c) => ({ id: c.xero_contact_id, name: c.xero_contact_name, status: c.xero_link_status, sync: c.xero_sync_status, error: c.xero_sync_error, notBilled: !!c.not_billed_in_xero });

// Client profile panel: linked Xero contact (from the mapping table) + Client Sync Status.
export function XeroContactPanel({ clientId, client, canEdit }) {
  const [link, setLink] = useState(pickLink(client));
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);

  useEffect(() => {
    if (!picking) return;
    const t = setTimeout(() => api.get(`/xero/contacts?q=${encodeURIComponent(q)}`).then((r) => setOpts(r.data)).catch(() => setOpts([])), 250);
    return () => clearTimeout(t);
  }, [q, picking]);

  const refresh = () => api.get(`/clients/${clientId}`).then((r) => setLink(pickLink(r.data)));
  async function run(fn, msg) {
    try { await fn(); await refresh(); setPicking(false); toast.success(msg); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  const save = (contact_id) => run(() => api.put(`/clients/${clientId}/xero-contact`, { contact_id }), contact_id ? "Xero contact linked" : "Xero contact unlinked");
  const suggested = link.id && link.status !== "confirmed";

  return (
    <div className="border border-[var(--line)] rounded-sm p-3 mb-3" data-testid="xero-contact-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs">
          <div className="label-overline mb-1 flex items-center gap-1"><Link2 size={11} /> Xero contact <SyncStatusPill status={link.sync} testid="xero-sync-status" /></div>
          {link.id ? (
            <span data-testid="xero-contact-name">{link.name}{" "}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${link.status === "confirmed" ? "border-[var(--ok)] text-[var(--ok)]" : "border-[var(--warn)] text-[var(--warn)]"}`} data-testid="xero-link-status">
                {link.status === "confirmed" ? "Confirmed" : "Suggested match — please confirm"}
              </span>
            </span>
          ) : <span className="text-[var(--ink-2)]" data-testid="xero-contact-none">Not linked</span>}
          {link.error && <div className="text-[10px] text-[var(--err)] mt-1" data-testid="xero-sync-error">{link.error}</div>}
        </div>
        {canEdit && (
          <div className="flex gap-1.5 flex-wrap justify-end">
            {suggested && <button type="button" data-testid="xero-confirm-btn" onClick={() => save(link.id)} className="btn-primary text-[11px] !py-1 !px-2 flex items-center gap-1"><Check size={11} /> Confirm</button>}
            {suggested && <button type="button" data-testid="xero-reject-btn" onClick={() => run(() => api.post(`/clients/${clientId}/xero-suggestion/reject`), "Suggestion rejected")} className="btn-ghost text-[11px] !py-1 !px-2">Reject</button>}
            {link.error && <button type="button" data-testid="xero-retry-btn" onClick={() => run(() => api.post(`/clients/${clientId}/xero-retry`), "Retried")} className="btn-ghost text-[11px] !py-1 !px-2 flex items-center gap-1"><RefreshCcw size={11} /> Retry</button>}
            <button type="button" data-testid="xero-change-btn" onClick={() => setPicking((p) => !p)} className="btn-ghost text-[11px] !py-1 !px-2">{link.id ? "Change" : "Link"}</button>
            {link.id && <button type="button" data-testid="xero-unlink-btn" onClick={() => save(null)} className="btn-ghost text-[11px] !py-1 !px-2" title="Unlink"><X size={11} /></button>}
          </div>
        )}
      </div>
      {canEdit && (
        <label className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)] mt-2 cursor-pointer">
          <input type="checkbox" data-testid="xero-not-billed-toggle" checked={link.notBilled}
            onChange={(e) => run(() => api.patch(`/clients/${clientId}/xero-billing`, { not_billed_in_xero: e.target.checked }), "Updated")} />
          Not billed in Xero
        </label>
      )}
      {picking && (
        <div className="mt-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
            <input data-testid="xero-contact-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Xero contacts…" className="input-slim w-full !pl-7 text-xs" />
          </div>
          <div className="max-h-44 overflow-y-auto mt-1 border border-[var(--line)] rounded-sm">
            {opts.length === 0 && <div className="text-[11px] text-[var(--ink-2)] p-2">No contacts found. Run Sync now on Finance to load Xero contacts.</div>}
            {opts.map((o) => (
              <button type="button" key={o.contact_id} data-testid={`xero-contact-opt-${o.contact_id}`} disabled={!!o.linked_client && o.contact_id !== link.id}
                onClick={() => save(o.contact_id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-[var(--bg)] disabled:opacity-50 flex justify-between gap-2">
                <span className="truncate">{o.name}{o.company_number ? ` · ${o.company_number}` : ""}</span>
                {o.linked_client && <span className="text-[10px] text-[var(--ink-2)] truncate">linked to {o.linked_client}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
